"use client";

import { Icons } from "@assets/assets";
import Image from "next/image";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { faqs } from "./data";
import DOMPurify from "isomorphic-dompurify";
interface FAQItem {
  question: string;
  answer: string;
}

interface FaqProps {
  FaqArray?: FAQItem[];
  allopen?: boolean;
  button?: React.ReactNode;
}

export default function FaqClient({
  FaqArray,
  allopen = false,
  button,
}: FaqProps) {
  // Memoized FAQ data (prevents recalculation)
  const faqData = useMemo(() => FaqArray ?? faqs, [FaqArray]);

  // Single active index for faster state updates
  const [activeIndex, setActiveIndex] = useState<number | null>(
    allopen ? 0 : null,
  );

  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [heights, setHeights] = useState<number[]>([]);

  const toggleFAQ = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  // Measure heights once
  useEffect(() => {
    setHeights(
      faqData?.map((_, i) => {
        const el = contentRefs.current[i];
        return el ? el.scrollHeight : 0;
      }),
    );
  }, [faqData]);

  return (
    <section className="faq bg-[#fff] w-full relative overflow-hidden" id="faq">
      <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] pt-[30px] lg:pt-[100px] relative z-1">
        {/* HEADER */}
        <div className="faq-header flex flex-col lg:flex-row justify-between items-start lg:items-center gap-[30px] lg:gap-[15px] pb-[5px] lg:pb-[60px]">
          <div className="faq-header-left leading-[110%] flex flex-col 2xl:flex-row 2xl:gap-[50px] gap-[20px] items-start">
            <p className="text-[#000] text-base font-medium uppercase tracking-tight min-w-[17ch]">
              FAQ's
            </p>

            <h2 className="text-[length:var(--section-title)] font-medium clash-font leading-[110%] max-w-[13ch]">
              <span className="text-[#225bd4] capitalize">Frequently</span>
              <span className="text-[#000]"> Asked Questions</span>
            </h2>
          </div>

          {button && (
            <div className="button-container lg:ml-auto self-start lg:self-center">
              {button}
            </div>
          )}
        </div>

        {/* ACCORDION */}
        <div className="faq-accordions pt-[20px] pb-[20px] lg:pt-[40px] lg:pb-[70px] max-w-[1250px] mx-auto flex flex-col">
          {faqData?.map((faq, index) => {
            const isOpen = activeIndex === index;
            const cleanHTML = DOMPurify.sanitize(faq.answer);
            return (
              <div
                key={faq.question}
                className="accordion-main border-b border-black/10 last:border-0 xl:py-[20px]"
              >
                {/* QUESTION */}
                <div
                  className="flex justify-between items-start cursor-pointer py-[15px] lg:py-[25px]"
                  onClick={() => toggleFAQ(index)}
                >
                  <h3
                    className="text-[#00040b] text-[clamp(1rem,0.3994rem+1.2644vw,2.375rem)] font-medium clash-font leading-[140%] max-w-[85%]"
                    dangerouslySetInnerHTML={{ __html: faq.question ?? "" }}
                  />

                  <Image
                    src={Icons?.arrowUp as string}
                    alt="arrow"
                    width={50}
                    height={50}
                    className={`lg:w-[50px] lg:h-[50px] w-[25px] h-[25px] object-contain transform transition-transform duration-300 ${
                      isOpen ? "rotate-0 opacity-100" : "opacity-50 rotate-180"
                    }`}
                    sizes="(max-width: 1024px) 100vw, 50vw"
                  />
                </div>

                {/* ANSWER */}
                <div
                  ref={(el) => {
                    contentRefs.current[index] = el;
                  }}
                  style={{
                    height: isOpen ? heights[index] : 0,
                  }}
                  className="transition-all duration-500 ease-in-out overflow-hidden"
                >
                  <div
                    className="text-[#636363] text-[length:var(--hero-para)] leading-[150%] pb-[25px]
                    [&_ul]:list-disc [&_ul]:pl-[35px] [&_ul]:mt-[20px] [&_ul]:mb-2 [&_ul]:font-medium 
                    [&_ol]:list-decimal [&_ol]:pl-[35px] [&_ol]:mt-2 [&_ol]:mb-2 [&_ol]:font-medium
                    [&_a]:text-[#225bd4] [&_ul>li]:marker:text-[#225bd4] [&_ol>li]:marker:text-[#225bd4]"
                    dangerouslySetInnerHTML={{ __html: cleanHTML }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
